/* eslint-disable no-param-reassign */

import _ from 'lodash';
import type { AxiosError, AxiosResponse } from 'axios';
import axios from 'axios';
import { sanitizeUrl } from '@braintree/sanitize-url';
import csrfTokenService from '@app/helpers/csrfTokenService';

export const BUNDLE_OUT_OF_DATE = 'bundle-out-of-date';

const LAST_RE_AUTH_AT_KEY = 'lastReAuthAt';

const USER_DELETED_MESSAGE = 'User deleted';

declare global {
  interface Window {
    bbBundleVersion?: string;
  }
}

const setAxiosInterceptors = () => {
  axios.interceptors.request.use(async config => {
    const csrfToken = await csrfTokenService.getToken();

    if (csrfToken) _.set(config, `headers['x-csrf-token']`, csrfToken);

    // Stream Chat SDK does not allow custom headers like bb-web-bundle-version
    if (config.url.includes('chat.stream-io')) {
      return config;
    }

    config.headers['bb-web-bundle-version'] = window?.bbBundleVersion;

    return config;
  });

  axios.interceptors.response.use(
    async (response: AxiosResponse) => {
      if (
        response.status !== 304 &&
        response.headers[BUNDLE_OUT_OF_DATE] === 'true'
      ) {
        window.sessionStorage.setItem(BUNDLE_OUT_OF_DATE, 'true');
      }
      return response;
    },
    async (error: AxiosError<string | { message: string }>) => {
      const { response } = error;

      if (!response) {
        return Promise.reject(error);
      }

      if (response.status === 401) {
        const lastReAuthAt = sessionStorage.getItem(LAST_RE_AUTH_AT_KEY)
          ? Date.parse(sessionStorage.getItem(LAST_RE_AUTH_AT_KEY)!)
          : new Date(0);

        const diff = Date.now().valueOf() - lastReAuthAt.valueOf();

        if (diff <= 1000) {
          // 1 second buffer for prevents duplicate operations on concurrent requests
          return Promise.reject(error);
        }

        if (diff <= 60 * 1000) {
          // Log user out if trying to re-auth more than once within 1 minute window
          sessionStorage.removeItem(LAST_RE_AUTH_AT_KEY);
          window.localStorage.setItem('loggedOut', 'true');
          window.location.href = '/logoutpage';
          return Promise.reject(error);
        }

        sessionStorage.setItem(
          LAST_RE_AUTH_AT_KEY,
          new Date(Date.now()).toISOString(),
        );
        sessionStorage.setItem('bburl', window.location.hash);

        /**
         * It is possible for the refresh_token to exceed its expiry date.
         * Making a request to the auth server to re-authenticate the user
         * will assign a new refresh_token to the user and will NOT require
         * re-authentication so long as their session is stored in the auth
         * server.
         */
        window.location.href = `/oauth/callback`;
        return Promise.reject(error);
      }

      if (response.status === 403 && response.data === USER_DELETED_MESSAGE) {
        window.localStorage.setItem('loggedOut', 'true');
        window.location.href = '/logoutpage';
      }

      if (
        response.data &&
        typeof response.data === 'object' &&
        'message' in response.data &&
        response.data.message === csrfTokenService.INVALID_CSRF_ERROR_MESSAGE
      ) {
        csrfTokenService.clearCsrfCache();
      }

      // MLS SSO booking errors
      if (
        (response.status === 400 || response.status === 404) &&
        response.headers.location
      ) {
        window.location.href = sanitizeUrl(response.headers.location);
      }

      return Promise.reject(error);
    },
  );
};

export default setAxiosInterceptors;
